import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Health-domain configuration + per-domain assessment surface (T-0104 /
 * C20-114).
 *
 * Two backend reads, both engagement-scoped per the URL grammar:
 *
 *   - GET /api/v1/engagements/:id/health/domains?is_active=
 *       Lists the configured domains for this engagement. Default filter
 *       is `is_active=true` server-side (the rating-UI hot path); pass
 *       `false` to see archived rows. The list shape mirrors
 *       `ListHealthDomainsResult` in
 *       `apps/api/src/services/health-domains.ts → HealthDomainDto`.
 *
 *   - GET /api/v1/engagements/:id/health/assessments?domain_id=&status=
 *       Lists assessment rows. The Domains screen uses this with
 *       `domain_id=<selected>` to surface the latest committed +
 *       in-progress rounds; the Overview screen uses
 *       `getOverallPosture()` (see `health-posture.ts`) which composes
 *       latest_rollup_color per domain in a single read.
 *
 * Companion read for the Overview screen — the engagement-level posture
 * rollup — lives in `health-posture.ts`. Per README §8 pattern 6 the
 * overall posture is **computed not stored** server-side; we just render
 * what the API returns and never let users edit it directly.
 */

export const DomainOriginSchema = z.enum(['firm_default', 'ai_proposed', 'manually_added']);
export type DomainOrigin = z.infer<typeof DomainOriginSchema>;

export const HealthDomainSchema = z.object({
  id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  name: z.string(),
  display_order: z.number(),
  description: z.string().nullable(),
  origin: DomainOriginSchema,
  is_active: z.boolean(),
  methodology_template_ref: z.string().nullable(),
  override_record_id: z.string().nullable(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type HealthDomain = z.infer<typeof HealthDomainSchema>;

export const ListHealthDomainsSchema = z.object({
  domains: z.array(HealthDomainSchema),
});
export type ListHealthDomainsResult = z.infer<typeof ListHealthDomainsSchema>;

export function listHealthDomains(
  engagementId: string,
  opts?: { is_active?: boolean },
): Promise<ListHealthDomainsResult> {
  const query: Record<string, string | undefined> = {};
  if (opts?.is_active !== undefined) {
    query.is_active = String(opts.is_active);
  }
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/domains`,
    ListHealthDomainsSchema,
    { query },
  );
}

export function useHealthDomainsQuery(
  engagementId: string | undefined,
  opts?: { is_active?: boolean },
) {
  return useQuery({
    queryKey: ['health', 'domains', engagementId, opts?.is_active ?? true] as const,
    queryFn: () => listHealthDomains(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

// ===========================================================================
// Per-domain assessments
// ===========================================================================

export const AssessmentStatusSchema = z.enum(['in_progress', 'committed']);
export type AssessmentStatus = z.infer<typeof AssessmentStatusSchema>;

export const DomainRatingSchema = z.enum(['healthy', 'at_risk', 'critical']);
export type DomainRating = z.infer<typeof DomainRatingSchema>;

export const RollupColorSchema = z.enum(['green', 'yellow_divergent', 'red', 'grey']);
export type RollupColor = z.infer<typeof RollupColorSchema>;

export const DomainAssessmentSchema = z.object({
  assessment_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  domain_id: z.string().uuid(),
  round_seq: z.number(),
  status: AssessmentStatusSchema,
  rating: DomainRatingSchema.nullable(),
  rollup_color: RollupColorSchema.nullable(),
  narrative: z.string().nullable(),
  evidence_summary: z.string().nullable(),
  confirmed_by_user_id: z.string().uuid().nullable(),
  confirmed_at: z.string().nullable(),
  ai_proposal_id: z.string().uuid().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DomainAssessment = z.infer<typeof DomainAssessmentSchema>;

export const ListAssessmentsSchema = z.object({
  items: z.array(DomainAssessmentSchema),
});
export type ListAssessmentsResult = z.infer<typeof ListAssessmentsSchema>;

export function listDomainAssessments(
  engagementId: string,
  opts?: { domain_id?: string; status?: AssessmentStatus },
): Promise<ListAssessmentsResult> {
  const query: Record<string, string | undefined> = {};
  if (opts?.domain_id) query.domain_id = opts.domain_id;
  if (opts?.status) query.status = opts.status;
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/assessments`,
    ListAssessmentsSchema,
    { query },
  );
}

export function useDomainAssessmentsQuery(
  engagementId: string | undefined,
  opts?: { domain_id?: string; status?: AssessmentStatus },
) {
  return useQuery({
    queryKey: ['health', 'assessments', engagementId, opts?.domain_id, opts?.status] as const,
    queryFn: () => listDomainAssessments(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

// ===========================================================================
// Domain configuration mutations (T-0070 / T-0114) — surface the
// PATCH /api/v1/engagements/:engagement_id/health/domains/:domain_id
// and POST .../health/domains/confirm endpoints to the domain editor
// modal. Soft-remove (PATCH is_active=false) and rename / reorder share
// the same patch hook; confirm RESETS the domain set (T-0219): to the
// firm's assessment defaults when the body is empty, or to the AI
// proposal / explicit list it names.
// ===========================================================================

export interface DomainOverride {
  tag: string;
  rationale: string;
}

export interface PatchDomainBody {
  is_active?: boolean;
  name?: string;
  display_order?: number;
  description?: string | null;
  override?: DomainOverride;
}

export interface PatchDomainInput {
  engagementId: string;
  domainId: string;
  body: PatchDomainBody;
}

const PatchDomainResponseSchema = z.object({ ok: z.boolean().optional() }).passthrough();

export function patchHealthDomain(input: PatchDomainInput): Promise<unknown> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(input.engagementId)}/health/domains/${encodeURIComponent(input.domainId)}`,
    PatchDomainResponseSchema,
    { method: 'PATCH', body: input.body },
  );
}

export function usePatchHealthDomainMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<PatchDomainInput, 'engagementId'>) => {
      if (!engagementId) {
        return Promise.reject(new Error('engagementId required'));
      }
      return patchHealthDomain({ engagementId, ...input });
    },
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['health', 'domains', engagementId] });
      }
    },
  });
}

export interface ConfirmHealthDomainsBody {
  proposal_id?: string | null;
  domains?: Array<{ name: string; description?: string | null; display_order?: number }>;
}

const ConfirmHealthDomainsResponseSchema = z.object({}).passthrough();

export function confirmHealthDomains(
  engagementId: string,
  body: ConfirmHealthDomainsBody,
): Promise<unknown> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/domains/confirm`,
    ConfirmHealthDomainsResponseSchema,
    { method: 'POST', body },
  );
}

export function useConfirmHealthDomainsMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConfirmHealthDomainsBody) => {
      if (!engagementId) {
        return Promise.reject(new Error('engagementId required'));
      }
      return confirmHealthDomains(engagementId, body);
    },
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['health', 'domains', engagementId] });
      }
    },
  });
}

// ===========================================================================
// T-0126 — single-row add for the "Add custom domain" affordance on the
// domain editor modal. Distinct from the bulk reset endpoint above; since
// T-0219 nothing gates on a methodology, so an add works on any engagement.
// ===========================================================================

export interface AddHealthDomainBody {
  name: string;
  description?: string | null;
  display_order?: number;
}

const AddHealthDomainResponseSchema = z.object({
  domain_id: z.string().uuid(),
});

export function addHealthDomain(
  engagementId: string,
  body: AddHealthDomainBody,
  idempotencyKey?: string,
): Promise<{ domain_id: string }> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/domains`,
    AddHealthDomainResponseSchema,
    {
      method: 'POST',
      body,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useAddHealthDomainMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: AddHealthDomainBody;
      idempotencyKey?: string;
    }) => {
      if (!engagementId) return Promise.reject(new Error('engagementId required'));
      return addHealthDomain(engagementId, body, idempotencyKey);
    },
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['health', 'domains', engagementId] });
      }
    },
  });
}

/**
 * Translate a `DomainRating` enum from the assessments API to the visual
 * `<PostureChip>` vocabulary used across the UI. The rating enum mirrors
 * the posture vocabulary verbatim except the `at_risk` underscore form
 * (vs. the chip's kebab `at-risk`).
 */
export function ratingToPostureStatus(
  r: DomainRating | null | undefined,
): 'healthy' | 'at-risk' | 'critical' | 'unassessed' {
  switch (r) {
    case 'healthy':
      return 'healthy';
    case 'at_risk':
      return 'at-risk';
    case 'critical':
      return 'critical';
    case null:
    case undefined:
      return 'unassessed';
  }
}

// ===========================================================================
// Assessment lifecycle (T-0072 backends — client wiring added in Phase 1)
// ===========================================================================

export const StartAssessmentResultSchema = z.object({
  assessment_id: z.string(),
  domain_id: z.string(),
  round_seq: z.number(),
  status: z.string(),
});

export function useStartAssessmentMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ domainId }: { domainId: string }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/domains/${encodeURIComponent(domainId)}/assessments`,
        StartAssessmentResultSchema,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'assessments', engagementId] });
    },
  });
}

export interface CommitAssessmentBody {
  rating: 'healthy' | 'at_risk' | 'critical';
  narrative?: string | null;
  evidence_summary?: string | null;
}

export const CommitAssessmentResultSchema = z
  .object({ assessment_id: z.string().optional(), status: z.string().optional() })
  .passthrough();

export function useCommitAssessmentMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assessmentId, body }: { assessmentId: string; body: CommitAssessmentBody }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/assessments/${encodeURIComponent(assessmentId)}/commit`,
        CommitAssessmentResultSchema,
        { method: 'PUT', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'assessments', engagementId] });
      void qc.invalidateQueries({ queryKey: ['health', 'posture', engagementId] });
      void qc.invalidateQueries({ queryKey: ['health-findings', engagementId] });
    },
  });
}
